<template>
  <div :class="$style.container">
    <Search
      v-if="initialized"
      :model="model"
      :search-placeholder="config?.placeholder"
      :search-synonym="searchSynonym"
      :search-synonym-suggest="searchSynonymSuggest"
      :selected-suggestion="selectedSuggestion"
      :show-did-you-mean="didYouMeanVisible"
      @active="getSearchResults"
      @change="handleSearchInputChange"
      @inactive="handleInactive"
      @initialize="handleSearchInputInitialize"
      @key-up-down="handleKeyDown"
      @key-up-enter="submitSearch"
      @key-up-up="handleKeyUp"
      @submitted="submitSearch"
    />
    <ul v-if="suggestions.length !== 0" v-show="active" :class="$style.suggestions">
      <li
        v-for="(suggestion, index) in suggestions"
        :key="index"
        :class="[$style.suggestion, { [$style.hover]: index === selectedSuggestionIndex }]"
        @click="() => handleSuggestionClick(suggestion.text)"
      >
        <span v-html="suggestion.text" />
      </li>
    </ul>
  </div>
</template>

<script setup>
import Search from '../Search/Search.vue';

import { computed, ref, watch } from 'vue';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { post } from '../../xhr/post';
import { get } from '../../xhr/get';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
  model: {
    type: Array,
    default: () => [],
  },
});

const storeConfiguration = useStoreConfiguration();

const emit = defineEmits(['submitted']);

const searchSynonymSuggest = ref([]);
const suggestions = ref([]);
const selectedSuggestion = ref('');
const active = ref(false);
const selectedSuggestionIndex = ref(-1);
const searchSynonym = ref(null);
const searchSynonyms = ref(null);
const initialized = ref(false);
const terms = ref([]);

const disableExternalDocuments = window.disableExternalDocuments;

const didYouMeanVisible = computed(() => {
  return !active.value || suggestions.value.length === 0;
});

const handleSearchInputInitialize = (term, preventSuggestions) => {
  handleSearchInputChange(term);
  if (!searchSynonym.value) {
    getSearchResults(term, preventSuggestions);
  }
};

const handleSearchInputChange = (term) => {
  searchSynonym.value = null;

  if (term.length > 0) {
    checkSearchSynonym(term);
  }
};

const findSearchSynonym = (synonyms, term) => {
  const termLowerCase = term.toLowerCase();

  for (const key in synonyms) {
    for (const workKey in synonyms[key]) {
      const word = synonyms[key][workKey];
      if (word.toLowerCase() === termLowerCase) {
        return key;
      }
    }
  }
};

const checkSearchSynonym = (term) => {
  const joinedTerms = term || terms.value.join(' ');
  searchSynonym.value = findSearchSynonym(searchSynonyms.value, joinedTerms);
};

const resetSearchSynonymSuggest = () => {
  searchSynonymSuggest.value = [];
};

const handleInactive = () => {
  emptySuggestions();
};

const submitSearch = (newTerms) => {
  resetSearchSynonymSuggest();

  if (props.config?.headerSearch) {
    const queryTerms = newTerms.toString().replace(/,/g, '+');
    const submitUrl = props.config.submitUrl + (queryTerms ? `?wika-search=${queryTerms}` : '');
    window.location = submitUrl;
  } else if (newTerms[0] !== '') {
    active.value = false;
    emit('submitted', newTerms);
  }
};

const computedCountry = computed(() => {
  return (
    storeConfiguration?.caasConfig?.searchCountry ??
    window?.searchCountry ??
    storeConfiguration?.caasConfig?.currentCountry ??
    window?.currentCountry ??
    storeConfiguration?.caasConfig?.locale
  );
});

const getSearchResults = async (newTerms, preventSuggestions) => {
  if (!newTerms.length || (newTerms.length === 1 && newTerms[0].length < 2)) {
    emptySuggestions();
  } else {
    await post(
      `${storeConfiguration.config?.pt_search_base_url}${storeConfiguration.config?.caas_suggestSearchUrl}`,
      {
        // global
        container: storeConfiguration.config?.pt_search_container,
        shopContainer: storeConfiguration.config?.pt_search_shop_container,
        country: computedCountry.value,
        language: storeConfiguration.caasConfig.searchCountry
          ? storeConfiguration.caasConfig.language?.replace('EN-US', 'EN')
          : storeConfiguration.caasConfig.language,
        disableExternalDocuments,
        instance: storeConfiguration.caasConfig.instance || window.instance,
        // payload
        word: newTerms,
      },
    )
      .then((response) => processSearch(response, preventSuggestions, newTerms))
      .catch(() => {
        processSearch({}, preventSuggestions, newTerms);
      });
  }
};

const emptySuggestions = () => {
  active.value = false;
  suggestions.value = [];
  resetSearchSynonymSuggest();
};

const processSynonyms = (data) => {
  if (suggestions.value.length === 0 && data.suggest && data.suggest['content-term-suggestion']) {
    const searchSynonymSuggest = data.suggest['content-term-suggestion']
      .map((word) => {
        if (word.options.length > 0) {
          return word.options.sort((a, b) => b.score - a.score)[0].text;
        }
      })
      .filter((word) => word);

    searchSynonymSuggest.value = searchSynonymSuggest;
  } else {
    resetSearchSynonymSuggest();
  }
};

const processSearch = (data, preventSuggestions) => {
  if (!preventSuggestions) {
    if (data.suggest && data.suggest['prefix-suggest'] && data.suggest['prefix-suggest'][0]) {
      active.value = true;
      suggestions.value = data.suggest['prefix-suggest'][0].options;
      selectedSuggestionIndex.value = -1;
    } else {
      console.error('Suggest data is not available');
    }
  }
  processSynonyms(data);
};

const handleSuggestionClick = (suggestion) => {
  selectedSuggestion.value = suggestion;
  active.value = false;
  submitSearch([suggestion]);
};

const handleKeyUp = () => {
  if (selectedSuggestionIndex.value > 0) {
    selectedSuggestionIndex.value--;
    selectedSuggestion.value = suggestions.value[selectedSuggestionIndex.value].text;
  }
};

const handleKeyDown = () => {
  if (selectedSuggestionIndex.value < suggestions.value.length - 1) {
    selectedSuggestionIndex.value++;
    selectedSuggestion.value = suggestions.value[selectedSuggestionIndex.value].text;
  }
};

watch(storeConfiguration, () => {
  if (storeConfiguration.config?.pt_search_synonym_json.route) {
    get(storeConfiguration.config.pt_search_synonym_json.route)
      .then((synonyms) => {
        searchSynonyms.value = synonyms;
        initialized.value = true;
      })
      .catch(() => {
        searchSynonyms.value = {};
        initialized.value = true;
      });
  }
});
</script>

<style module lang="scss">
@forward './ElasticSearch.scss';
</style>
